/**
 * RezervaceDostupnost — the availability strip of the booking form: the car's other reservations around the chosen
 * term, drawn by day over a window the server sized (the term plus the turnaround gap and a week), the gaps hatched
 * and the proposed term dashed, so a collision is seen before saving. It is the servis event modal's day timeline
 * (`ServisOverlapPreview`) in days instead of half hours. What collides is the server's answer, never decided here.
 */

import { dayIndex, rozlozeni } from './dostupnost-helpers';
import type { Dostupnost } from './api';

const LANE = 20;

interface Props {
  data: Dostupnost;
  /** The term the answer was asked for, `YYYY-MM-DD`. */
  term: { od: string; do: string };
  /** Prague today — its column is marked. */
  today: string;
  /** A newer answer is on the way: the strip is drawn dimmed. */
  nacita?: boolean;
}

const HATCH = 'repeating-linear-gradient(45deg, var(--surface-2) 0 4px, transparent 4px 8px)';

export function RezervaceDostupnost({ data, term, today, nacita = false }: Props) {
  const l = rozlozeni(data, term);
  const n = l.dny.length;
  const pct = (i: number) => `${(i / n) * 100}%`;
  const todayIdx = dayIndex(data.okno_od, today);
  const height = 6 + l.lanes * LANE;

  return (
    <div data-testid="rezervace-dostupnost" className={'flex flex-col gap-2 border border-line bg-bg/40 px-3 py-3 ' + (nacita ? 'opacity-60' : '')}>
      <div className="flex items-center justify-between font-mono text-[9.5px] uppercase tracking-[0.08em] text-ink-3">
        <span>Obsazenost vozidla</span>
        {data.mezera_dni > 0 && (
          <span>
            Mezi zápůjčkami {data.mezera_dni} {data.mezera_dni === 1 ? 'den' : data.mezera_dni < 5 ? 'dny' : 'dní'}
          </span>
        )}
      </div>

      {/* day scale */}
      <div className="relative h-3">
        {l.popisky.map(({ i, text }) => (
          <span
            key={i}
            className="absolute -translate-x-1/2 whitespace-nowrap font-mono text-[9px] text-ink-3"
            style={{ left: `calc(${pct(i)} + ${100 / n / 2}%)` }}
          >
            {text}
          </span>
        ))}
      </div>

      <Track label="Obsazeno" height={height} dny={l.dny} todayIdx={todayIdx}>
        {l.bloky.map((b) => (
          <span key={b.id} data-testid={`dostupnost-rez-${b.id}`} data-konflikt={b.konflikt ? '1' : '0'}>
            {[b.mezeraPred, b.mezeraPo].map(
              (m, k) =>
                m && (
                  <span
                    key={k}
                    aria-hidden
                    className="absolute border-y border-line"
                    style={{
                      left: pct(m.from),
                      width: `${((m.to - m.from + 1) / n) * 100}%`,
                      top: 3 + b.lane * LANE,
                      height: LANE - 4,
                      backgroundImage: HATCH,
                    }}
                  />
                ),
            )}
            <span
              title={b.title}
              className={
                'absolute flex items-center overflow-hidden whitespace-nowrap border px-1 text-[9.5px] font-medium ' +
                (b.poTerminu
                  ? 'border-red bg-red-soft text-red-text'
                  : b.konflikt
                    ? 'border-warn bg-warn-soft text-warn' // a collision outranks the block's own colour: it names what is in the way
                    : b.servis
                      ? 'border-red bg-red-soft text-red-text'
                      : b.technicky
                      ? 'border-line bg-neutral-soft text-ink-2'
                      : 'border-line-2 bg-surface-2 text-ink-2')
              }
              style={{
                left: `calc(${pct(b.from)} + 1px)`,
                width: `calc(${((b.to - b.from + 1) / n) * 100}% - 2px)`,
                top: 3 + b.lane * LANE,
                height: LANE - 4,
              }}
            >
              <span className="truncate">{b.label}{b.otevreny ? ' ›' : ''}</span>
            </span>
          </span>
        ))}
        {l.bloky.length === 0 && (
          <span className="absolute inset-0 grid place-items-center text-[10.5px] text-ink-3">Žádná jiná rezervace</span>
        )}
      </Track>

      {l.servis.length > 0 && (
        <Track label="Servis" height={6 + l.servisLanes * LANE} dny={l.dny} todayIdx={todayIdx}>
          {l.servis.map((b) => (
            <span
              key={b.id}
              data-testid={`dostupnost-servis-${b.id}`}
              data-konflikt={b.konflikt ? '1' : '0'}
              title={b.title}
              className={
                'absolute flex items-center overflow-hidden whitespace-nowrap border px-1 text-[9.5px] font-medium ' +
                (b.konflikt ? 'border-red bg-red-soft text-red-text' : 'border-line-2 bg-surface-2 text-ink-2')
              }
              style={{
                left: `calc(${pct(b.from)} + 1px)`,
                width: `calc(${((b.to - b.from + 1) / n) * 100}% - 2px)`,
                top: 3 + b.lane * LANE,
                height: LANE - 4,
              }}
            >
              <span className="truncate">{b.label}</span>
            </span>
          ))}
        </Track>
      )}

      <Track label="Nová" height={LANE + 6} dny={l.dny} todayIdx={todayIdx}>
        {l.navrh && (
          <span
            data-testid="dostupnost-navrh"
            title="Nová rezervace"
            className="absolute flex items-center overflow-hidden border border-dashed border-accent bg-accent-soft px-1 text-[9.5px] font-medium text-accent-text"
            style={{
              left: `calc(${pct(l.navrh.from)} + 1px)`,
              width: `calc(${((l.navrh.to - l.navrh.from + 1) / n) * 100}% - 2px)`,
              top: 3,
              height: LANE - 4 + 6,
            }}
          >
            <span className="truncate">Nová rezervace</span>
          </span>
        )}
      </Track>
    </div>
  );
}

function Track({
  label,
  height,
  dny,
  todayIdx,
  children,
}: {
  label: string;
  height: number;
  dny: Array<{ date: string; vikend: boolean }>;
  todayIdx: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-[52px] shrink-0 font-mono text-[9px] uppercase tracking-[0.08em] text-ink-3">{label}</span>
      <div className="relative flex-1 overflow-hidden border border-line bg-paper" style={{ height }}>
        {/* the day columns behind the blocks: weekends shaded, today marked */}
        <div className="absolute inset-0 flex" aria-hidden>
          {dny.map((d, i) => (
            <span
              key={d.date}
              className={'flex-1 border-l border-line first:border-l-0 ' + (i === todayIdx ? 'bg-accent-soft' : d.vikend ? 'bg-neutral-soft' : '')}
            />
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}
